// @flow

import classNames from 'classnames';
import React, { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import type { Node } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Button } from '@riseart/common';
import { cart as ENUM_CART } from 'Enum';
import { GOOGLE_TAGMANAGER_ACTIONS } from 'shared_data/providers/google/tagmanager/Actions';
import { actionFactory } from 'shared_services/riseart/utils/Utils';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { FavoriteButton } from 'shared_data/providers/queries/FavoriteButton';
import { AddCartItemButton } from 'shared_data/providers/queries/AddCartItem';

import {
  visualSearchCardMenuCls,
  visualSearchCardMenuOverlayCls,
  visualSearchCardMenuOpenedCls,
} from 'shared_components/forms/search/visual/Visual.less';

type Props = {
  opened: boolean,
  onBackToResult: Function,
  item: Object,
  wrapperElm: Object,
  overlay?: boolean,
};

/**
 * VisualSearchCardMenu
 *
 * @param {Props} props
 * @returns {Node}
 */
export function VisualSearchCardMenu({
  wrapperElm,
  opened,
  onBackToResult,
  item,
  overlay = false,
}: Props): Node {
  const menuWrapperRef = useRef(null);
  const dispatch = useDispatch();
  const [position, setPosition] = useState(-1000);
  const callGtmAction = useCallback(
    (action: string, payload?: Object = {}) => {
      dispatch(actionFactory(action, { artwork: item, ...payload }));
    },
    [dispatch, item],
  );

  useEffect(() => {
    if (menuWrapperRef.current && wrapperElm) {
      setPosition(
        wrapperElm.getBoundingClientRect().left -
          menuWrapperRef.current.offsetParent.getBoundingClientRect().left,
      );
    }
  }, [wrapperElm]);

  const handleBackToResults = useCallback(() => {
    callGtmAction(GOOGLE_TAGMANAGER_ACTIONS.VISUAL_SEARCH_MENU_CLICK, { button: 'backToResults' });
    onBackToResult();
  }, [callGtmAction, onBackToResult]);

  return (
    <div
      ref={menuWrapperRef}
      className={classNames(
        visualSearchCardMenuCls,
        overlay && visualSearchCardMenuOverlayCls,
        opened && visualSearchCardMenuOpenedCls,
      )}
      {...(wrapperElm && menuWrapperRef.current ? { style: { left: `${position}px` } } : null)}
    >
      {opened ? (
        <Fragment>
          {typeof onBackToResult === 'function' ? (
            <button type="button" onClick={handleBackToResults}>
              <FormattedMessage id="forms.search.visual.actions.backToResult" />
            </button>
          ) : null}
          <Button
            isAnchor
            type="link"
            to={UrlAssembler.artDetail(item.id, item.slug)}
            target="_blank"
            onClick={() => {
              callGtmAction(GOOGLE_TAGMANAGER_ACTIONS.VISUAL_SEARCH_MENU_CLICK, {
                button: 'details',
              });
            }}
          >
            <FormattedMessage id="forms.search.visual.actions.seeArtwork" />
          </Button>
          <AddCartItemButton
            skuId={item.skuBuyId}
            cartId={ENUM_CART.type.TYPE_BUY}
            qty={1}
            onLoading={false}
            openSidebar
          >
            {({ loading, onClick }) => (
              <button
                type="button"
                onClick={
                  loading
                    ? null
                    : () => {
                        callGtmAction(GOOGLE_TAGMANAGER_ACTIONS.VISUAL_SEARCH_MENU_CLICK, {
                          button: 'addToCart',
                        });
                        onClick();
                      }
                }
              >
                <FormattedMessage id="forms.search.visual.actions.addToBasket" />
              </button>
            )}
          </AddCartItemButton>
          <FavoriteButton artId={item.id}>
            {({ isFavorited, isLoading, onClick }) => (
              <button
                type="button"
                onClick={
                  isLoading
                    ? null
                    : () => {
                        callGtmAction(GOOGLE_TAGMANAGER_ACTIONS.VISUAL_SEARCH_MENU_CLICK, {
                          button: 'addToWishlist',
                        });
                        onClick();
                      }
                }
              >
                <FormattedMessage
                  id={isFavorited ? 'common.removeFromWishlist' : 'common.addToWishlist'}
                />
              </button>
            )}
          </FavoriteButton>
        </Fragment>
      ) : null}
    </div>
  );
}
